/**
 * Keyboard, mouse look, and pointer lock — plus a virtual layer for touch.
 *
 * Touch controls do not get their own input path. They write into the same
 * abstractions the keyboard uses: virtual key codes merged into isDown() and
 * sample(), an analog move vector added to WASD, and look deltas injected
 * where mouse movement would land. Every consumer — stance resolution, the
 * transmit dial, breath-holding, key bindings — works identically on both,
 * because none of them can tell which device produced the input.
 */

export interface InputState {
  forward: number;
  strafe: number;
  sprint: boolean;
  crouch: boolean;
  interact: boolean;
  /** Accumulated mouse delta since the last read, in radians. */
  yawDelta: number;
  pitchDelta: number;
}

const SENSITIVITY = 0.0022;

export class Input {
  readonly state: InputState = {
    forward: 0,
    strafe: 0,
    sprint: false,
    crouch: false,
    interact: false,
    yawDelta: 0,
    pitchDelta: 0,
  };

  private readonly keys = new Set<string>();
  /** Touch-held "keys". A separate set so window blur cannot clear a thumb. */
  private readonly virtualKeys = new Set<string>();
  /** Analog joystick vector, each axis -1..1, summed with WASD in sample(). */
  private virtualMove = { forward: 0, strafe: 0 };
  private locked = false;
  private readonly onceHandlers = new Map<string, Array<() => void>>();
  private readonly wheelHandlers: Array<(direction: number) => void> = [];
  private readonly clickHandlers: Array<(button: number) => void> = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('blur', () => this.keys.clear());
  }

  /** Mouse wheel, as -1 (up) or +1 (down). Only fires while pointer-locked. */
  onScroll(handler: (direction: number) => void): void {
    this.wheelHandlers.push(handler);
  }

  /** Mouse button press. Only fires while pointer-locked. */
  onClick(handler: (button: number) => void): void {
    this.clickHandlers.push(handler);
  }

  private onWheel = (e: WheelEvent): void => {
    if (!this.locked) return;
    e.preventDefault();
    const direction = Math.sign(e.deltaY);
    if (direction === 0) return;
    for (const handler of this.wheelHandlers) handler(direction);
  };

  private onMouseDown = (e: MouseEvent): void => {
    // Unlocked clicks belong to the pointer-lock prompt, not the game.
    if (!this.locked) return;
    for (const handler of this.clickHandlers) handler(e.button);
  };

  /** Register a one-shot handler for a key, fired on press. */
  on(code: string, handler: () => void): void {
    const list = this.onceHandlers.get(code) ?? [];
    list.push(handler);
    this.onceHandlers.set(code, list);
  }

  /**
   * Take the pointer, asking for raw device deltas.
   *
   * `movementX/Y` normally arrives with the operating system's pointer
   * acceleration curve already applied — roughly 1:1 while you ease the mouse
   * around, and multiplied hard once you move quickly. For a cursor that is
   * what you want; for a first-person camera it means a fast flick throws the
   * view further than your hand went, and inconsistently, because the curve is
   * non-linear. Slow rotation stays perfectly well behaved, which is what
   * makes it read as a bug rather than as a sensitivity setting.
   *
   * `unadjustedMovement` asks the browser to skip the curve and hand over the
   * device's own counts, so the view tracks the mouse one-to-one at every
   * speed. There is no server-side look validation to fight with here — the
   * server stores yaw and pitch verbatim — so this is the whole of it.
   */
  requestLock(): void {
    const canvas = this.canvas as HTMLCanvasElement & {
      requestPointerLock(options?: { unadjustedMovement?: boolean }): Promise<void> | undefined;
    };

    let pending: Promise<void> | undefined;
    try {
      pending = canvas.requestPointerLock({ unadjustedMovement: true });
    } catch {
      // Older browsers throw on an unexpected argument rather than ignoring it.
      pending = undefined;
      void (this.canvas as HTMLCanvasElement).requestPointerLock();
      return;
    }

    // Chrome returns a promise and rejects where unadjusted movement is not
    // available; browsers that predate the option return undefined and have
    // already taken the lock, so there is nothing left to do.
    pending?.catch(() => {
      void (this.canvas as HTMLCanvasElement).requestPointerLock();
    });
  }

  releaseLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  get isLocked(): boolean {
    return this.locked;
  }

  private onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) this.keys.clear();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    // Let the browser keep its own shortcuts working outside the game.
    if (e.code === 'F5' || e.code === 'F12') return;
    if (this.locked) e.preventDefault();

    if (!this.keys.has(e.code)) {
      for (const handler of this.onceHandlers.get(e.code) ?? []) handler();
    }
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.state.yawDelta -= e.movementX * SENSITIVITY;
    this.state.pitchDelta -= e.movementY * SENSITIVITY;
  };

  isDown(code: string): boolean {
    return this.keys.has(code) || this.virtualKeys.has(code);
  }

  // -------------------------------------------------------------------------
  // Virtual layer — the touch overlay's writing end
  // -------------------------------------------------------------------------

  /** Fire a key's one-shot bindings as if it had just been pressed. */
  press(code: string): void {
    for (const handler of this.onceHandlers.get(code) ?? []) handler();
  }

  /**
   * Hold or release a virtual key. Continuous consumers (breath-hold on
   * Space, the transmit dial on A/D) read it through isDown() and sample()
   * exactly as they would a physical key.
   */
  setVirtualKey(code: string, down: boolean): void {
    if (down) this.virtualKeys.add(code);
    else this.virtualKeys.delete(code);
  }

  /** Joystick vector, each axis -1..1. Overrides nothing; sums with WASD. */
  setVirtualMove(forward: number, strafe: number): void {
    this.virtualMove.forward = forward;
    this.virtualMove.strafe = strafe;
  }

  /** Touch-drag look, already in radians. */
  injectLook(yawDelta: number, pitchDelta: number): void {
    this.state.yawDelta += yawDelta;
    this.state.pitchDelta += pitchDelta;
  }

  /** Dispatch a click binding (0 = left) without a mouse or pointer lock. */
  simulateClick(button: number): void {
    for (const handler of this.clickHandlers) handler(button);
  }

  /** Refresh continuous inputs. Mouse deltas are consumed by the caller. */
  sample(): InputState {
    const s = this.state;
    const down = (code: string): boolean => this.keys.has(code) || this.virtualKeys.has(code);

    s.forward = clampAxis(
      (down('KeyW') ? 1 : 0) - (down('KeyS') ? 1 : 0) + this.virtualMove.forward,
    );
    s.strafe = clampAxis(
      (down('KeyD') ? 1 : 0) - (down('KeyA') ? 1 : 0) + this.virtualMove.strafe,
    );
    s.sprint = down('ShiftLeft') || down('ShiftRight');
    s.crouch = down('ControlLeft') || down('KeyC');
    // Space is not sampled here: it has no continuous meaning of its own any
    // more, only the contextual one-shots bound in Game (leave a ladder,
    // switch spectator subject) and the breath-hold, which reads isDown.
    s.interact = down('KeyE');
    return s;
  }

  consumeLook(): { yaw: number; pitch: number } {
    const out = { yaw: this.state.yawDelta, pitch: this.state.pitchDelta };
    this.state.yawDelta = 0;
    this.state.pitchDelta = 0;
    return out;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('wheel', this.onWheel);
  }
}

function clampAxis(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

/**
 * Coarse-pointer detection, once at load.
 *
 * `pointer: coarse` is the capability that actually matters — "the primary
 * pointing device is imprecise" — rather than user-agent guessing. A
 * touchscreen laptop with a trackpad stays on desktop controls, which is
 * correct: it has a keyboard.
 */
export const IS_TOUCH_DEVICE: boolean =
  typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
